/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import { FunctionComponent, useLayoutEffect, useMemo, useRef, useState } from 'react';

import { RootState, useTivoSelector } from '../../../core/tivo/tivo-redux-wrapper';
import { Mousetrap } from '../../common';

import './PersonItem.scss';

type PersonItemProps = {
    title: string;
    imageUrl: string;
    role: string[];
    personId: string;
    index: number;
    gridType?: string;
};

const PersonItem: FunctionComponent<PersonItemProps> = ({
    title,
    imageUrl,
    index,
    gridType = '',
}) => {
    const titleRef = useRef<HTMLSpanElement>(null);

    const [showFade, setShowFade] = useState(false);

    const focusedItem = useTivoSelector(
        (state: RootState) => state.grid[gridType]?.focusedItem || { row: 0, column: 0 },
    );
    const isGridFocused = useTivoSelector(
        (state: RootState) => state.grid[gridType]?.isActive || false,
    );
    const { columnLimit } = useTivoSelector((state: RootState) => state.grid[gridType]);

    // Calculate row and column for grid positioning and mouse interaction
    const row = useMemo(() => Math.floor(index / columnLimit), [index, columnLimit]);
    const column = useMemo(() => index % columnLimit, [index, columnLimit]);

    const highlighted = useMemo(() => {
        return isGridFocused && focusedItem.row === row && focusedItem.column === column;
    }, [isGridFocused, focusedItem.row, focusedItem.column, row, column]);
    const mouseValue = `cast-grid_${row}_${column}`;

    useLayoutEffect(() => {
        const personTitle = titleRef.current;
        if (personTitle) {
            const isOverflowing = personTitle.scrollHeight - personTitle.clientHeight > 1;
            setShowFade(isOverflowing);
        }
    }, []);

    return (
        <Mousetrap value={mouseValue}>
            <div className={`person-item-container ${highlighted ? 'highlighted' : ''}`}>
                <div className="person-item-image-container">
                    <img src={imageUrl} alt={title} className="person-item-image" />
                </div>
                <div className="person-item-title-container">
                    <span className={`person-item-title ${showFade ? 'fade' : ''}`} ref={titleRef}>
                        {title}
                    </span>
                </div>
            </div>
        </Mousetrap>
    );
};

export default PersonItem;
